import React, { useEffect, useRef } from "react";
import { BaseIndicatorProps } from "../../lib/interface/Indicator.interface";
import { LineDto } from "../../lib/interface/LineDto.interface";
import { ISeriesApi, LineSeries, Time } from "lightweight-charts";
import { INDICATOR_COLORS } from "../../lib/constants/chart.constants";
import { debugLog } from "@/lib/utils/debug";

/**
 * Props specific to Plasma Lines indicator
 */
export interface PlasmaLinesProps extends BaseIndicatorProps {
  plasmaLines: LineDto[];
  formatTime: (timestamp: number) => number;
}

/**
 * Plasma Lines Indicator Component
 * Shows plasma trend lines on the chart
 * Manages its own series creation and cleanup based on isEnabled prop
 */
export const PlasmaLinesIndicator: React.FC<PlasmaLinesProps> = ({
  chartInstance,
  isEnabled,
  plasmaLines,
  formatTime,
}) => {
  const plasmaSeriesRef = useRef<ISeriesApi<"Line">[]>([]);

  useEffect(() => {
    if (!chartInstance) return;

    // Clear existing plasma series
    plasmaSeriesRef.current.forEach((series) => {
      try {
        chartInstance.removeSeries(series);
      } catch {
        // Silently ignore removal errors
      }
    });
    plasmaSeriesRef.current = [];

    // Only create new series if enabled and we have data
    if (isEnabled && plasmaLines.length > 0) {
      debugLog("PlasmaLines: creating", plasmaLines.length, "series");

      const newSeries = plasmaLines.map((line) => {
        const series = chartInstance.addSeries(LineSeries, {
          color: INDICATOR_COLORS.plasma,
          lineWidth: 1,
          lineStyle: 1,
        });

        series.setData([
          {
            time: formatTime(line.timeStart) as Time,
            value: Number(line.priceStart),
          },
          {
            time: formatTime(line.timeEnd) as Time,
            value: Number(line.priceEnd),
          },
        ]);

        return series;
      });

      plasmaSeriesRef.current = newSeries;
    } else if (!isEnabled) {
      debugLog("PlasmaLines: disabled");
    }

    // Cleanup function to remove series when component unmounts or dependencies change
    return () => {
      plasmaSeriesRef.current.forEach((series) => {
        try {
          chartInstance.removeSeries(series);
        } catch {
          // Silently ignore cleanup errors
        }
      });
      plasmaSeriesRef.current = [];
    };
  }, [chartInstance, plasmaLines, isEnabled, formatTime]);

  // This is a logic-only component that manages chart series
  return null;
};
